import { dashboardT } from '../../../../../../lib/messages';
import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link2, Settings, Target } from 'lucide-react';
import { useForm, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dealFormSchema, DealEditorTab, type DealFormValue } from '../../../model';
import { DealGeneralFields } from '../DealGeneralFields';
import { DealIntegrationsFields } from '../DealIntegrationsFields';
import { DealTargetingFields } from '../DealTargetingFields';

const tabs = [
  { value: DealEditorTab.General, label: 'General', icon: Settings },
  { value: DealEditorTab.Targeting, label: 'Targeting', icon: Target },
  { value: DealEditorTab.DemandIntegrations, label: 'Demand integrations', icon: Link2 },
] as const;

export function DealEditorForm(props: {
  initialValue: DealFormValue;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: DealFormValue) => Promise<void>;
}) {
  'use no memo';
  const [editorSection, setActiveTab] = useEditorSectionControls(
    DealEditorTab.General,
    Object.values(DealEditorTab),
  );
  const form = useForm<DealFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(dealFormSchema),
  });

  function invalidSubmit(errors: FieldErrors<DealFormValue>) {
    if (errors.targetings) setActiveTab(DealEditorTab.Targeting);
    else if (errors.demandIntegration) setActiveTab(DealEditorTab.DemandIntegrations);
    else setActiveTab(DealEditorTab.General);
  }

  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {
      [DealEditorTab.Targeting]: ['targetings'],
      [DealEditorTab.DemandIntegrations]: ['demandIntegration'],
    },
    DealEditorTab.General,
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) => void form.handleSubmit(props.onSubmit, invalidSubmit)(event)}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            {tabs.map(({ value, label, icon: Icon }) => (
              <DashboardPageFormTab
                key={value}
                value={value}
                errorCount={tabErrors[value]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Icon className="h-4 w-4" aria-hidden="true" />}
              >
                {label}
              </DashboardPageFormTab>
            ))}
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={DealEditorTab.General}>
            <DealGeneralFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={DealEditorTab.Targeting}>
            <DealTargetingFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={DealEditorTab.DemandIntegrations}>
            <DealIntegrationsFields />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={props.saving}>
            {props.saving ? 'Saving…' : 'Save'}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}
